/**
 * Reservations Actions Module
 *
 * Handles reservation action functions for the index/list page.
 * The main reservations.js handles form functionality and is a separate bundle.
 */

import { getToken } from '../utils/csrf.js';

const BASE_URL = '/reservations';

/**
 * Helper to submit a form action
 * @param {string} action - The action URL
 * @param {Object} extraFields - Additional form fields
 */
function submitAction(action, extraFields = {}) {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = action;

    const token = document.createElement('input');
    token.type = 'hidden';
    token.name = '_token';
    token.value = getToken();
    form.appendChild(token);

    // Add any extra fields
    for (const [name, value] of Object.entries(extraFields)) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
    }

    document.body.appendChild(form);
    form.submit();
}

/**
 * Confirm a reservation
 * @param {string} id - Reservation ID
 */
export function confirmBooking(id) {
    if (confirm('Confirm this reservation?')) {
        submitAction(`${BASE_URL}/${id}/confirm`);
    }
}

/**
 * Seat a customer for their reservation
 * @param {string} id - Reservation ID
 */
export function seatCustomer(id) {
    const table = prompt('Enter table number (optional):');
    const extraFields = {};

    if (table) {
        extraFields.table_number = table;
    }

    submitAction(`${BASE_URL}/${id}/seat`, extraFields);
}

/**
 * Cancel a reservation
 * @param {string} id - Reservation ID
 */
export function cancelBooking(id) {
    const reason = prompt('Reason for cancellation (optional):');

    // User clicked Cancel on prompt
    if (reason === null) {
        return;
    }

    const extraFields = {};
    if (reason) {
        extraFields.cancellation_reason = reason;
    }

    submitAction(`${BASE_URL}/${id}/cancel`, extraFields);
}

/**
 * Mark a reservation as no-show
 * @param {string} id - Reservation ID
 */
export function markNoShow(id) {
    if (confirm('Mark this reservation as a no-show?')) {
        submitAction(`${BASE_URL}/${id}/no-show`);
    }
}

/**
 * Complete a reservation (party has left)
 * @param {string} id - Reservation ID
 */
export function completeBooking(id) {
    if (confirm('Mark this reservation as completed?')) {
        submitAction(`${BASE_URL}/${id}/complete`);
    }
}

/**
 * Filter reservations by date
 * @param {HTMLInputElement} input - Date input element
 */
export function filterByDate(input) {
    const date = input.value;
    const params = new URLSearchParams(window.location.search);

    if (date) {
        params.set('date', date);
    } else {
        params.delete('date');
    }

    window.location.href = BASE_URL + (params.toString() ? '?' + params.toString() : '');
}

/**
 * Filter reservations by status
 * @param {HTMLSelectElement} select - Status select element
 */
export function filterByStatus(select) {
    const status = select.value;
    const params = new URLSearchParams(window.location.search);

    if (status) {
        params.set('status', status);
    } else {
        params.delete('status');
    }

    window.location.href = BASE_URL + (params.toString() ? '?' + params.toString() : '');
}

/**
 * Clear all reservation filters
 */
export function clearFilters() {
    window.location.href = BASE_URL;
}

// Expose globally for legacy onclick handlers
window.confirmBooking = confirmBooking;
window.seatCustomer = seatCustomer;
window.cancelBooking = cancelBooking;
window.markNoShow = markNoShow;
window.completeBooking = completeBooking;
window.filterByDate = filterByDate;
window.filterByStatus = filterByStatus;

export default {
    confirmBooking,
    seatCustomer,
    cancelBooking,
    markNoShow,
    completeBooking,
    filterByDate,
    filterByStatus,
    clearFilters
};
